import { DqTable } from "@decentriq/components";
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Stack, Tooltip, Typography } from "@mui/joy";
import { type MRT_ColumnDef, type MRT_Row } from "material-react-table";
import { memo, useMemo } from "react";
import {
  mediaDataRoomOrganizationRolePresentationMap,
  type MediaDataRoomParticipant,
  type MediaDataRoomParticipantPermission,
  type MediaDataRoomParticipantRole,
} from "features/mediaDataRoom/models";
import { mediaDataRoomOrganizationPermissionsAsColumnHeaders } from "../models";

interface MediaDataRoomConfigurationPermissionsTableProps {
  participants: MediaDataRoomParticipant[];
}

const MediaDataRoomConfigurationPermissionsTable =
  memo<MediaDataRoomConfigurationPermissionsTableProps>(({ participants }) => {
    const allAvailablePermissions = useMemo(() => {
      return [
        ...new Set(
          participants
            .map((participant) => Object.values(participant.permissions))
            .flat()
        ),
      ].sort(
        (a, b) =>
          mediaDataRoomOrganizationPermissionsAsColumnHeaders[a].order -
          mediaDataRoomOrganizationPermissionsAsColumnHeaders[b].order
      );
    }, [participants]);
    const permissionsTableDefinition = useMemo<
      MRT_ColumnDef<MediaDataRoomParticipant>[]
    >(() => {
      return [
        {
          accessorKey: "name",
          header: "Participant",
        },
        {
          Cell: ({ cell }) => {
            const role = cell.getValue<MediaDataRoomParticipantRole>();
            return mediaDataRoomOrganizationRolePresentationMap[role];
          },
          accessorKey: "role",
          header: "Role",
          size: 100,
        },
        ...allAvailablePermissions.map(
          (permission: MediaDataRoomParticipantPermission) => {
            const { title: header, tooltip } =
              mediaDataRoomOrganizationPermissionsAsColumnHeaders[permission];
            return {
              Cell: ({ row }: { row: MRT_Row<MediaDataRoomParticipant> }) => {
                return (
                  <Stack direction="row" justifyContent="center" width="100%">
                    <Checkbox
                      checked={row.original.permissions.includes(permission)}
                      readOnly={true}
                    />
                  </Stack>
                );
              },
              Header: () => (
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={1}
                  justifyContent="center"
                  justifyItems="center"
                  width="100%"
                >
                  {header}
                  &nbsp;
                  <Tooltip
                    title={
                      <Typography
                        level="body-sm"
                        sx={{
                          color: "white",
                        }}
                      >
                        {tooltip}
                      </Typography>
                    }
                  >
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </Tooltip>
                </Stack>
              ),
              accessorKey: permission,
              header,
              muiTableHeadCellProps: {
                align: "center" as const,
              },
            };
          }
        ),
      ];
    }, [allAvailablePermissions]);
    return (
      <DqTable
        columns={permissionsTableDefinition}
        data={participants}
        getRowId={(row) => row.id.toString()}
        muiTablePaperProps={{
          sx: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          },
        }}
      />
    );
  });

MediaDataRoomConfigurationPermissionsTable.displayName =
  "MediaDataRoomConfigurationPermissionsTable";

export default MediaDataRoomConfigurationPermissionsTable;
